import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';

import { useTranslate } from 'src/locales';
import Iconify from 'src/components/iconify';
import {GetProfileOutput} from "@/types";

// ----------------------------------------------------------------------

type Props = {
  profile: GetProfileOutput;
};

// ----------------------------------------------------------------------

export function UserProfileStats({profile}: Props) {
  const { t } = useTranslate();

  const stats = [
    {
      label: t('page.user.profile.stats.comments') as string,
      value: profile.commentCount ?? 0,
      icon: 'solar:chat-round-line-bold',
      color: 'primary.main',
    },
    {
      label: t('page.user.profile.stats.recipes') as string,
      value: profile.recipeCount ?? 0,
      icon: 'solar:chef-hat-bold',
      color: 'success.main',
    },
    {
      label: t('page.user.profile.stats.ratings') as string,
      value: profile.ratingCount ?? 0,
      icon: 'solar:star-bold',
      color: 'warning.main',
    },
    {
      label: t('page.user.profile.stats.average_rating') as string,
      value: profile.ratingAverage ?? 0,
      icon: 'solar:star-bold',
      color: 'error.main',
      isDecimal: true,
    },
  ];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('page.user.profile.stats.title')}
        </Typography>

        <Grid container sx={{ mt: 3 }}>
          {stats.map((stat, index) => (
            <Grid item xs={6} sm={3} key={stat.label}>
              <Box
                sx={{
                  textAlign: 'center',
                  py: 2,
                  ...(index < stats.length - 1 && {
                    borderRight: { xs: 0, sm: 1 },
                    borderColor: { xs: 'transparent', sm: 'divider' },
                  }),
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'background.neutral',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                  }}
                >
                  <Iconify icon={stat.icon} width={24} sx={{color: stat.color}} />
                </Box>

                <Typography variant="h4" sx={{mb: 0.5}}>
                  {(stat as any).isDecimal
                    ? stat.value.toFixed(1)
                    : stat.value.toLocaleString()}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}

